import React, { FunctionComponent, useState } from "react";
import { MosaicElementType, WeatherForecastType, WeatherParameters } from "../../../../api/mosaicApi";
import { Button } from "../../../components/Button";
import FormCheckbox from "../../../components/formRow/FormCheckbox";
import { FormInput, FormInputColorpicker, FormInputNumber, FormRow, FormSelect, SelectOption } from "../../../components/formRow/FormRow";
import { SelectAvatarButton } from "../../../components/selectFileButton/SelectFileButton";
import SvgSelector from "../../../components/svgSelector/SvgSelector";
import { BaseWidgetData, WidgetPositionProps, usePositionHandlers, useImageUpload, makeNumericHandler } from "./widgetShared";
import { WidgetPositionControls } from "./WidgetPositionControls";

export type WeatherWidgetData = BaseWidgetData<WeatherParameters>;

interface WeatherWidgetProps extends WidgetPositionProps {
    onAddWidget: (data: WeatherWidgetData) => void;
    onBackgroundImageUrlChange?: (url: string) => void;
}

const ForecastTypeOptions: SelectOption[] = [
    { name: 'Hourly', value: '0' },
    { name: 'Daily', value: '1' }
];

export const WeatherWidget: FunctionComponent<WeatherWidgetProps> = (props) => {
    const {
        columnStart, width, rowStart, height,
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange,
        isOutOfLimit, maxElements, onAddWidget, validationError,
        onBackgroundImageUrlChange
    } = props;

    const [noBackground, setNoBackground] = useState<boolean>(false);
    const [city, setCity] = useState<string>('');
    const [showCurrentWeather, setShowCurrentWeather] = useState<boolean>(true);
    const [showForecast, setShowForecast] = useState<boolean>(true);
    const [forecastType, setForecastType] = useState<number>(WeatherForecastType.Hourly);
    const [itemCount, setItemCount] = useState<number | string>(5);
    const [updateIntervalMinutes, setUpdateIntervalMinutes] = useState<number | string>(30);
    const [textColour, setTextColour] = useState<string>('#ffffff');
    const [backgroundColour, setBackgroundColour] = useState<string>('#000000');
    const [cityNameFontSize, setCityNameFontSize] = useState<number | string>('');
    const [weatherDescriptionFontSize, setWeatherDescriptionFontSize] = useState<number | string>('');
    const [currentTemperatureFontSize, setCurrentTemperatureFontSize] = useState<number | string>('');
    const [windFontSize, setWindFontSize] = useState<number | string>('');
    const [aqiFontSize, setAqiFontSize] = useState<number | string>('');
    const [forecastTimeFontSize, setForecastTimeFontSize] = useState<number | string>('');
    const [forecastTempFontSize, setForecastTempFontSize] = useState<number | string>('');
    const [forecastIconSize, setForecastIconSize] = useState<number | string>('');
    const [currentIconSize, setCurrentIconSize] = useState<number | string>('');
    const [leftPadding, setLeftPadding] = useState<number | string>('');
    const [spaceBetweenCurrentWeatherAndAirQuality, setSpaceBetweenCurrentWeatherAndAirQuality] = useState<number | string>('');
    const [spaceBetweenAirQualityAndForecast, setSpaceBetweenAirQualityAndForecast] = useState<number | string>('');
    const [showAqi, setShowAqi] = useState<boolean>(false);
    const [showGrassPollen, setShowGrassPollen] = useState<boolean>(false);
    const [showBirchPollen, setShowBirchPollen] = useState<boolean>(false);
    const [showRagweedPollen, setShowRagweedPollen] = useState<boolean>(false);
    const [showAlderPollen, setShowAlderPollen] = useState<boolean>(false);
    const [showMugwortPollen, setShowMugwortPollen] = useState<boolean>(false);
    const [showOlivePollen, setShowOlivePollen] = useState<boolean>(false);

    const positionHandlers = usePositionHandlers({
        onColumnStartChange, onWidthChange, onRowStartChange, onHeightChange
    });

    const bgUpload = useImageUpload({
        onUrlChange: onBackgroundImageUrlChange,
    });

    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value);
    const handleForecastTypeChange = (value: string) => setForecastType(+value);

    const resetState = () => {
        setCity('');
        setShowCurrentWeather(true);
        setShowForecast(true);
        setForecastType(WeatherForecastType.Hourly);
        setItemCount(5);
        setUpdateIntervalMinutes(30);
        bgUpload.reset();
        setNoBackground(false);
        setTextColour('#ffffff');
        setBackgroundColour('#000000');
        setCityNameFontSize('');
        setWeatherDescriptionFontSize('');
        setCurrentTemperatureFontSize('');
        setWindFontSize('');
        setAqiFontSize('');
        setForecastTimeFontSize('');
        setForecastTempFontSize('');
        setForecastIconSize('');
        setCurrentIconSize('');
        setLeftPadding('');
        setSpaceBetweenCurrentWeatherAndAirQuality('');
        setSpaceBetweenAirQualityAndForecast('');
        setShowAqi(false);
        setShowGrassPollen(false);
        setShowBirchPollen(false);
        setShowRagweedPollen(false);
        setShowAlderPollen(false);
        setShowMugwortPollen(false);
        setShowOlivePollen(false);
    };

    const handleAddWidget = () => {
        const parameters: WeatherParameters = {
            type: "weather",
            city: city,
            showCurrentWeather: showCurrentWeather ? undefined : false,
            showForecast: showForecast ? undefined : false,
            forecastType: forecastType,
            itemCount: itemCount === '' ? undefined : +itemCount,
            updateIntervalMinutes: updateIntervalMinutes === '' ? undefined : +updateIntervalMinutes,
            backgroundImageRecourceBlobName: bgUpload.imageName || undefined,
            backgroundImageUrl: bgUpload.imageUrl || undefined,
            noBackground: noBackground || undefined,
            textColour: textColour,
            backgroundColour: backgroundColour,
            cityNameFontSize: cityNameFontSize === '' ? undefined : +cityNameFontSize,
            weatherDescriptionFontSize: weatherDescriptionFontSize === '' ? undefined : +weatherDescriptionFontSize,
            currentTemperatureFontSize: currentTemperatureFontSize === '' ? undefined : +currentTemperatureFontSize,
            windFontSize: windFontSize === '' ? undefined : +windFontSize,
            aqiFontSize: aqiFontSize === '' ? undefined : +aqiFontSize,
            forecastTimeFontSize: forecastTimeFontSize === '' ? undefined : +forecastTimeFontSize,
            forecastTempFontSize: forecastTempFontSize === '' ? undefined : +forecastTempFontSize,
            forecastIconSize: forecastIconSize === '' ? undefined : +forecastIconSize,
            currentIconSize: currentIconSize === '' ? undefined : +currentIconSize,
            leftPadding: leftPadding === '' ? undefined : +leftPadding,
            spaceBetweenCurrentWeatherAndAirQuality: spaceBetweenCurrentWeatherAndAirQuality === '' ? undefined : +spaceBetweenCurrentWeatherAndAirQuality,
            spaceBetweenAirQualityAndForecast: spaceBetweenAirQualityAndForecast === '' ? undefined : +spaceBetweenAirQualityAndForecast,
            showAqi: showAqi || undefined,
            showGrassPollen: showGrassPollen || undefined,
            showBirchPollen: showBirchPollen || undefined,
            showRagweedPollen: showRagweedPollen || undefined,
            showAlderPollen: showAlderPollen || undefined,
            showMugwortPollen: showMugwortPollen || undefined,
            showOlivePollen: showOlivePollen || undefined
        };

        onAddWidget({
            type: MosaicElementType.Weather,
            startColumn: +columnStart,
            width: +width,
            startRow: +rowStart,
            height: +height,
            name: city,
            parameters: parameters
        });

        resetState();
    };

    return (
        <>
            <div style={{ minHeight: '180px' }}>
                <WidgetPositionControls
                    idPrefix="weather"
                    columnStart={columnStart}
                    width={width}
                    rowStart={rowStart}
                    height={height}
                    handlers={positionHandlers}
                    isOutOfLimit={isOutOfLimit}
                    maxElements={maxElements}
                    validationError={validationError}
                />
                
                {!isOutOfLimit && (
                    <div className="weather-widget" style={{ display: 'block' }}>
                        {/* Basic Settings */}
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInput
                                    errorMessage="Please enter city"
                                    id="weather_city"
                                    isError={false}
                                    title="City"
                                    onChange={handleCityChange}
                                    value={city}
                                    placeholderTxt="Enter city name"
                                />
                            </FormRow>
                        </div>

                        <div className="d-flex gap15">
                            <FormRow>
                                <FormSelect
                                    id="weather_forecast_type"
                                    title="Forecast Type"
                                    selectedValue={forecastType.toString()}
                                    isError={false}
                                    errorMessage="Error"
                                    options={ForecastTypeOptions}
                                    onChange={handleForecastTypeChange}
                                    hideDefaultValue={true}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_item_count"
                                    title="Item Count"
                                    isError={false}
                                    errorMessage="Please enter item count"
                                    onChangeValue={makeNumericHandler(setItemCount)}
                                    onChange={() => {}}
                                    value={itemCount}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_update_interval"
                                    title="Update Interval (min)"
                                    isError={false}
                                    errorMessage="Please enter update interval"
                                    onChangeValue={makeNumericHandler(setUpdateIntervalMinutes)}
                                    onChange={() => {}}
                                    value={updateIntervalMinutes}
                                />
                            </FormRow>
                        </div>

                        {/* Background Image */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Background</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap', marginBottom: '10px' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_no_background" checked={noBackground} onChange={setNoBackground} />
                                <span style={{ marginLeft: '8px' }}>No Background</span>
                            </div>
                        </div>
                        {!noBackground && (
                        <div className="d-flex gap15 align-items-center">
                            <FormRow>
                                <div style={{ paddingTop: '15px' }}>
                                    <SelectAvatarButton id="weather-bg" title="Upload Background" onChange={bgUpload.handleFileChange} />
                                </div>
                            </FormRow>
                            {bgUpload.imageUrl && (
                                <img 
                                    className="mosaic-img" 
                                    src={bgUpload.imageUrl} 
                                    alt="Background preview" 
                                    style={{ maxHeight: '100px', width: 'auto', objectFit: 'contain' }} 
                                />
                            )}
                        </div>
                        )}

                        {/* Colors */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Colors</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_text_colour"
                                    title="Text Color"
                                    isError={false}
                                    errorMessage="Please enter text color"
                                    onChangeValue={(value: string) => setTextColour(value)}
                                    onChange={() => {}}
                                    value={textColour}
                                />
                            </FormRow>
                            {!noBackground && (
                            <FormRow>
                                <FormInputColorpicker
                                    id="weather_background_colour"
                                    title="Background Color"
                                    isError={false}
                                    errorMessage="Please enter background color"
                                    onChangeValue={(value: string) => setBackgroundColour(value)}
                                    onChange={() => {}}
                                    value={backgroundColour}
                                />
                            </FormRow>
                            )}
                        </div>

                        {/* Font Sizes - Current Weather */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Font Sizes - Current Weather</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_city_name_font_size"
                                    title="City Name"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setCityNameFontSize)}
                                    onChange={() => {}}
                                    value={cityNameFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_description_font_size"
                                    title="Description"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setWeatherDescriptionFontSize)}
                                    onChange={() => {}}
                                    value={weatherDescriptionFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_current_temp_font_size"
                                    title="Temperature"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setCurrentTemperatureFontSize)}
                                    onChange={() => {}}
                                    value={currentTemperatureFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_wind_font_size"
                                    title="Wind"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setWindFontSize)}
                                    onChange={() => {}}
                                    value={windFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_aqi_font_size"
                                    title="AQI"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setAqiFontSize)}
                                    onChange={() => {}}
                                    value={aqiFontSize}
                                />
                            </FormRow>
                        </div>

                        {/* Font Sizes - Forecast */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Font Sizes - Forecast</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_time_font_size"
                                    title="Time"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setForecastTimeFontSize)}
                                    onChange={() => {}}
                                    value={forecastTimeFontSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_temp_font_size"
                                    title="Temperature"
                                    isError={false}
                                    errorMessage="Please enter font size"
                                    onChangeValue={makeNumericHandler(setForecastTempFontSize)}
                                    onChange={() => {}}
                                    value={forecastTempFontSize}
                                />
                            </FormRow>
                        </div>

                        {/* Icon Sizes */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Icon Sizes</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_current_icon_size"
                                    title="Current Icon Size"
                                    isError={false}
                                    errorMessage="Please enter icon size"
                                    onChangeValue={makeNumericHandler(setCurrentIconSize)}
                                    onChange={() => {}}
                                    value={currentIconSize}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_forecast_icon_size"
                                    title="Forecast Icon Size"
                                    isError={false}
                                    errorMessage="Please enter icon size"
                                    onChangeValue={makeNumericHandler(setForecastIconSize)}
                                    onChange={() => {}}
                                    value={forecastIconSize}
                                />
                            </FormRow>
                        </div>

                        {/* Padding & Spacing */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Padding & Spacing</h4>
                        <div className="d-flex gap15">
                            <FormRow>
                                <FormInputNumber
                                    id="weather_left_padding"
                                    title="Left Padding"
                                    isError={false}
                                    errorMessage="Please enter padding"
                                    onChangeValue={makeNumericHandler(setLeftPadding)}
                                    onChange={() => {}}
                                    value={leftPadding}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_space_current_aqi"
                                    title="Space: Weather - AQI"
                                    isError={false}
                                    errorMessage="Please enter spacing"
                                    onChangeValue={makeNumericHandler(setSpaceBetweenCurrentWeatherAndAirQuality)}
                                    onChange={() => {}}
                                    value={spaceBetweenCurrentWeatherAndAirQuality}
                                />
                            </FormRow>
                            <FormRow>
                                <FormInputNumber
                                    id="weather_space_aqi_forecast"
                                    title="Space: AQI - Forecast"
                                    isError={false}
                                    errorMessage="Please enter spacing"
                                    onChangeValue={makeNumericHandler(setSpaceBetweenAirQualityAndForecast)}
                                    onChange={() => {}}
                                    value={spaceBetweenAirQualityAndForecast}
                                />
                            </FormRow>
                        </div>

                        {/* Section Visibility */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Section Visibility</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_current" checked={showCurrentWeather} onChange={setShowCurrentWeather} />
                                <span style={{ marginLeft: '8px' }}>Current Weather</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_forecast" checked={showForecast} onChange={setShowForecast} />
                                <span style={{ marginLeft: '8px' }}>Forecast</span>
                            </div>
                        </div>

                        {/* Air Quality Visibility */}
                        <h4 style={{ marginTop: '20px', marginBottom: '10px' }}>Air Quality Visibility</h4>
                        <div className="d-flex gap15" style={{ flexWrap: 'wrap' }}>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_show_aqi" checked={showAqi} onChange={setShowAqi} />
                                <span style={{ marginLeft: '8px' }}>AQI</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_grass_pollen" checked={showGrassPollen} onChange={setShowGrassPollen} />
                                <span style={{ marginLeft: '8px' }}>Grass Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_birch_pollen" checked={showBirchPollen} onChange={setShowBirchPollen} />
                                <span style={{ marginLeft: '8px' }}>Birch Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_ragweed_pollen" checked={showRagweedPollen} onChange={setShowRagweedPollen} />
                                <span style={{ marginLeft: '8px' }}>Ragweed Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_alder_pollen" checked={showAlderPollen} onChange={setShowAlderPollen} />
                                <span style={{ marginLeft: '8px' }}>Alder Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_mugwort_pollen" checked={showMugwortPollen} onChange={setShowMugwortPollen} />
                                <span style={{ marginLeft: '8px' }}>Mugwort Pollen</span>
                            </div>
                            <div className="d-flex align-items-center" style={{ marginRight: '20px' }}>
                                <FormCheckbox id="weather_olive_pollen" checked={showOlivePollen} onChange={setShowOlivePollen} />
                                <span style={{ marginLeft: '8px' }}>Olive Pollen</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <div className="mosaic-settings-add-elem align-items-center" style={{ marginTop: '20px', marginBottom: '15px' }}>
                <Button
                    color="green"
                    label="Add Weather Widget"
                    onClick={handleAddWidget}
                    isDisabled={isOutOfLimit || city === ''}
                />
            </div>
        </>
    );
};

// Helper functions for Weather widget
export const getWeatherWidgetIcon = () => <SvgSelector title="weather" />;
export const getWeatherWidgetName = () => 'Weather';
export const getWeatherWidgetLimit = () => 3;

export default WeatherWidget;
